import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Paper,
  Alert,
} from '@mui/material';
import { useWaitlist } from '../hooks/useWaitlist';

const Splash: React.FC = () => {
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState<'tutor' | 'client' | ''>('');
  const [comments, setComments] = useState('');

  const { submitWaitlist, loading, error, submitted } = useWaitlist();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitWaitlist(email, userType, comments);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          pt: 10,
          pb: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Join the Future of Tutoring
          </Typography>
          <Typography variant="h5" component="p" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto' }}>
            We are building a marketplace that is more equitable for tutors and more efficient for those looking to hire tutors.
            We believe that by providing a platform that is more transparent and fair, both parties can succeed.
          </Typography>
        </Container>
      </Box>

      {/* Signup Section */}
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {submitted ? (
          <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h4" color="primary" gutterBottom fontWeight="bold">
              Thank you for signing up!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              We'll notify you as soon as we launch. Get ready to experience tutoring done right.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={3} sx={{ p: 5, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center" sx={{ mb: 4 }}>
              Get Early Access
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
              Sign up to be notified when we launch and get exclusive early access to our platform.
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                margin="normal"
                variant="outlined"
              />

              <FormControl component="fieldset" margin="normal" required fullWidth>
                <FormLabel component="legend" sx={{ mb: 1 }}>I am a:</FormLabel>
                <RadioGroup
                  value={userType}
                  onChange={(e) => setUserType(e.target.value as 'tutor' | 'client')}
                >
                  <FormControlLabel
                    value="tutor"
                    control={<Radio />}
                    label="Tutor - I want to teach and earn"
                  />
                  <FormControlLabel
                    value="client"
                    control={<Radio />}
                    label="Student - I want to learn"
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                fullWidth
                label="We'd love to hear your feedback!"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                multiline
                rows={4}
                margin="normal"
                variant="outlined"
                placeholder="What features would you like to see? Any suggestions?"
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={!email || !userType || loading}
                sx={{ mt: 3, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
              >
                {loading ? 'Submitting...' : 'Notify Me'}
              </Button>

              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Paper>
        )}
      </Container>

      {/* Mission Statement Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Why YoTutor?
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            We charge our tutors significantly less in fees while providing more features over time.
            We can do this because we are a team of software engineers, so our development overhead costs are extremely low.
            We're committed to creating a platform where both tutors and students thrive.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Splash;
